import type { ReactNode } from "react";
import { Link } from "gatsby";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import styled, { css } from "styled-components";

import { bp, colors, fonts, gutter, radius, shadow } from "../theme";

export const Container = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding-left: ${gutter};
  padding-right: ${gutter};
`;

export const Section = styled.section<{ $cream?: boolean }>`
  background: ${(p) => (p.$cream ? colors.section : "transparent")};
  padding-top: 72px;
  padding-bottom: 72px;

  ${bp.mobile} {
    padding-top: 32px;
    padding-bottom: 32px;
  }
`;

export const Stack = styled.div<{ $gap?: string; $mobileGap?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "24px"};

  ${bp.mobile} {
    gap: ${(p) => p.$mobileGap || p.$gap || "16px"};
  }
`;

export const Grid = styled.div<{
  $cols: number;
  $tabletCols?: number;
  $smallCols?: number;
  $gap?: string;
}>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols}, minmax(0, 1fr));
  gap: ${(p) => p.$gap || "20px"};

  ${bp.mobile} {
    grid-template-columns: repeat(${(p) => p.$tabletCols ?? 2}, minmax(0, 1fr));
    gap: 12px;
  }

  ${bp.small} {
    grid-template-columns: repeat(${(p) => p.$smallCols ?? 1}, minmax(0, 1fr));
  }
`;

export const OnDesktop = styled.span`
  ${bp.mobile} {
    display: none;
  }
`;

export const OnMobile = styled.span`
  ${bp.desktop} {
    display: none;
  }
`;

export const Eyebrow = styled.div`
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${colors.goldText};

  ${bp.mobile} {
    font-size: 11px;
  }
`;

export const PageTitle = styled.h1`
  font-size: clamp(30px, 5vw, 48px);
  line-height: 1.15;
`;

export const SectionTitle = styled.h2`
  font-size: clamp(26px, 3.4vw, 40px);
  line-height: 1.2;
`;

export const Lead = styled.p`
  font-size: 17px;
  line-height: 1.65;
  color: ${colors.text};

  ${bp.mobile} {
    font-size: 15px;
    line-height: 1.6;
  }
`;

export const Text = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${colors.text};

  ${bp.mobile} {
    font-size: 15px;
  }
`;

export const PageHeader = ({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children?: ReactNode;
}) => (
  <Stack $gap="14px" $mobileGap="10px" style={{ maxWidth: 760 }}>
    <Eyebrow>{eyebrow}</Eyebrow>
    <PageTitle>{title}</PageTitle>
    {children}
  </Stack>
);

const SectionHeadRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 40px;

  ${bp.mobile} {
    margin-bottom: 16px;
  }
`;

export const SectionHead = ({
  title,
  to,
  linkLabel,
}: {
  title: string;
  to?: string;
  linkLabel?: string;
}) => (
  <SectionHeadRow>
    <SectionTitle>{title}</SectionTitle>
    {to && (
      <TextLink to={to} style={{ flex: "none" }}>
        {linkLabel} →
      </TextLink>
    )}
  </SectionHeadRow>
);

const pill = css<{ $variant?: "primary" | "soft" | "light" }>`
  display: inline-block;
  text-align: center;
  border-radius: ${radius.pill};
  padding: 15px 30px;
  font-weight: 700;
  font-size: 15px;
  transition: filter 150ms ease;

  ${(p) =>
    p.$variant === "soft"
      ? css`
          background: ${colors.goldSoft};
          color: ${colors.goldText};
        `
      : css`
          background: ${colors.goldLight};
          color: ${colors.ink};
        `}

  &:hover {
    filter: brightness(1.06);
    color: ${(p) => (p.$variant === "soft" ? colors.goldText : colors.ink)};
  }

  ${bp.mobile} {
    padding: 14px 24px;
  }
`;

export const PillLink = styled(Link)<{
  $variant?: "primary" | "soft" | "light";
}>`
  ${pill}
`;

export const PillAnchor = styled.a<{ $variant?: "primary" | "soft" | "light" }>`
  ${pill}
`;

export const TextLink = styled(Link)`
  color: ${colors.goldText};
  font-weight: 700;
  font-size: 15px;

  ${bp.mobile} {
    font-size: 13px;
  }
`;

export const UnderlinedLink = styled(Link)`
  color: ${colors.goldText};
  font-weight: 700;
  font-size: 15px;
  border-bottom: 2px solid ${colors.goldBorder};
  padding-bottom: 2px;
`;

export const Card = styled.div<{ $dark?: boolean }>`
  background: ${(p) => (p.$dark ? colors.ink : colors.section)};
  color: ${(p) => (p.$dark ? colors.onDark : colors.ink)};
  border-radius: ${radius.lg};
  padding: 36px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${bp.mobile} {
    padding: 22px;
    border-radius: ${radius.md};
    gap: 10px;
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
`;

const CardName = styled.div`
  font-family: ${fonts.title};
  font-size: 26px;

  ${bp.mobile} {
    font-size: 21px;
  }
`;

const Price = styled.div<{ $dark?: boolean }>`
  font-family: ${fonts.title};
  font-size: 30px;
  white-space: nowrap;
  color: ${(p) => (p.$dark ? colors.goldLight : colors.gold)};

  ${bp.mobile} {
    font-size: 24px;
  }
`;

const PriceUnit = styled.span<{ $dark?: boolean }>`
  font-family: ${fonts.body};
  font-size: 14px;
  color: ${(p) => (p.$dark ? "rgba(253, 251, 246, .7)" : colors.muted)};

  ${bp.mobile} {
    font-size: 12px;
  }
`;

const CardList = styled.ul<{ $dark?: boolean }>`
  margin: 0;
  padding-left: 20px;
  font-size: 15px;
  line-height: 1.9;
  color: ${(p) => (p.$dark ? "rgba(253, 251, 246, .9)" : colors.text)};

  a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  a:hover {
    color: ${(p) => (p.$dark ? colors.goldLight : colors.goldText)};
  }

  ${bp.mobile} {
    font-size: 14px;
    line-height: 1.8;
  }
`;

export const PriceCard = ({
  name,
  price,
  unit,
  items,
  dark,
  children,
}: {
  name: string;
  price: string;
  unit: string;
  items?: ReactNode[];
  dark?: boolean;
  children?: ReactNode;
}) => (
  <Card $dark={dark}>
    <CardTop>
      <CardName>{name}</CardName>
      <Price $dark={dark}>
        {price}
        <PriceUnit $dark={dark}>{unit}</PriceUnit>
      </Price>
    </CardTop>
    {items && (
      <CardList $dark={dark}>
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </CardList>
    )}
    {children}
  </Card>
);

const Note = styled.div`
  border-top: 2px solid ${colors.gold};
  padding-top: 18px;
  font-size: 15px;
  line-height: 1.7;
  color: ${colors.text};

  strong {
    color: ${colors.ink};
  }

  a {
    color: ${colors.goldText};
    font-weight: 700;
  }

  ${bp.mobile} {
    padding-top: 14px;
    font-size: 14px;
  }
`;

export const NoteCard = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Note>
    <strong>{title}</strong> {children}
  </Note>
);

const coverStyles = css<{ $height?: string; $mobileHeight?: string }>`
  position: relative;
  overflow: hidden;
  border-radius: ${radius.lg};
  height: ${(p) => p.$height || "100%"};

  .gatsby-image-wrapper {
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
  }

  ${bp.mobile} {
    border-radius: ${radius.md};
    height: ${(p) => p.$mobileHeight || p.$height || "100%"};
  }
`;

const Cover = styled.div<{ $height?: string; $mobileHeight?: string }>`
  ${coverStyles}
`;

const TileButton = styled.button<{ $height?: string; $mobileHeight?: string }>`
  ${coverStyles}
  border: 0;
  padding: 0;
  margin: 0;
  display: block;
  width: 100%;
  cursor: zoom-in;
  font: inherit;
  color: inherit;
  text-align: left;
  background: transparent;
`;

const Caption = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(transparent, rgba(20, 18, 14, 0.85));
  color: ${colors.onDark};
  padding: 48px 24px 22px;

  ${bp.mobile} {
    padding: 34px 16px 12px;
  }
`;

const CaptionTitle = styled.div`
  font-weight: 700;
  font-size: 18px;

  ${bp.mobile} {
    font-size: 15px;
  }
`;

const CaptionSub = styled.div`
  font-size: 15px;
  opacity: 0.85;

  ${bp.mobile} {
    font-size: 13px;
  }
`;

export const PhotoTile = ({
  image,
  alt,
  title,
  sub,
  height,
  mobileHeight,
  onClick,
  className,
}: {
  image?: IGatsbyImageData;
  alt: string;
  title?: string;
  sub?: string;
  height?: string;
  mobileHeight?: string;
  onClick?: () => void;
  className?: string;
}) => {
  const content = (
    <>
      {image && <GatsbyImage image={image} alt={alt} objectFit="cover" />}
      {title && (
        <Caption>
          <CaptionTitle>{title}</CaptionTitle>
          {sub && <CaptionSub>{sub}</CaptionSub>}
        </Caption>
      )}
    </>
  );

  if (onClick) {
    return (
      <TileButton
        type="button"
        onClick={onClick}
        aria-label={`Agrandir la photo : ${title || alt}`}
        $height={height}
        $mobileHeight={mobileHeight}
        className={className}
      >
        {content}
      </TileButton>
    );
  }

  return (
    <Cover $height={height} $mobileHeight={mobileHeight} className={className}>
      {content}
    </Cover>
  );
};

export const Figure = styled(Cover)`
  border-radius: ${radius.xl};
  box-shadow: none;
`;

export const Split = styled(Container)`
  display: grid;
  grid-template-columns: 1fr 1.05fr;
  grid-template-areas:
    "title photo"
    "body photo";
  column-gap: 56px;
  row-gap: 20px;
  align-items: center;

  ${bp.mobile} {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "title"
      "photo"
      "body";
    row-gap: 16px;
  }
`;

export const SplitTitle = styled.div`
  grid-area: title;
  display: flex;
  flex-direction: column;
  gap: 14px;

  ${bp.mobile} {
    gap: 10px;
  }
`;

export const SplitBody = styled.div`
  grid-area: body;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;

  ${bp.mobile} {
    gap: 14px;
  }
`;

export const SplitPhoto = styled.div<{
  $height?: string;
  $mobileHeight?: string;
}>`
  ${coverStyles}
  grid-area: photo;
  border-radius: ${radius.xl};
  height: ${(p) => p.$height || "460px"};

  ${bp.mobile} {
    border-radius: ${radius.lg};
    height: ${(p) => p.$mobileHeight || "220px"};
  }
`;

const StatValue = styled.div`
  font-family: ${fonts.title};
  font-size: 32px;
  line-height: 1.1;

  ${bp.mobile} {
    font-size: 24px;
  }
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${colors.muted};

  ${bp.mobile} {
    font-size: 12px;
  }
`;

export const Stat = ({ value, label }: { value: string; label: string }) => (
  <div>
    <StatValue>{value}</StatValue>
    <StatLabel>{label}</StatLabel>
  </div>
);

export const Badge = styled.span`
  background: ${colors.bg};
  border: 1px solid ${colors.border};
  border-radius: ${radius.pill};
  padding: 9px 18px;
  font-size: 14px;
  color: ${colors.text};
`;

export const Callout = styled.div`
  background: ${colors.section};
  border-radius: ${radius.lg};
  padding: 28px 32px;
  font-size: 16px;
  line-height: 1.7;
  color: ${colors.text};

  strong {
    color: ${colors.ink};
  }

  ${bp.mobile} {
    padding: 18px 20px;
    font-size: 14px;
    border-radius: ${radius.md};
  }
`;

export const DarkBanner = styled.div`
  background: ${colors.ink};
  color: ${colors.onDark};
  border-radius: ${radius.lg};
  padding: 36px;

  ${bp.mobile} {
    padding: 22px;
    border-radius: ${radius.md};
  }
`;

export const FloatingCard = styled.div`
  position: absolute;
  left: 24px;
  bottom: 24px;
  background: ${colors.bg};
  border-radius: ${radius.md};
  padding: 18px 22px;
  box-shadow: ${shadow.card};
  display: flex;
  gap: 14px;
  align-items: center;
  font-weight: 700;
  font-size: 15px;
  line-height: 1.3;
`;
